/**
 * Tokens del tema de PrimeNG (TcgPreset). Módulo HOJA: no importa nada, a propósito.
 *
 * Vivían en `app.config.ts`, y eso salía caro en los tests: `app.config.spec.ts` sólo necesita
 * este objeto de tokens, pero al importarlo de `app.config.ts` arrastraba `app.routes.ts` —con
 * sus ~70 `loadComponent: () => import(…)`— al bundle de la suite. Un único spec de cuatro
 * aserciones sobre colores hacía compilar el árbol de rutas perezosas entero. Medido en local, misma
 * caché caliente y mismo worktree: el bundle de tests baja de 1.192 ficheros de salida a 972 (de 593
 * a 483 chunks `.js`) y el build de 24,3 s a 15,9 s.
 *
 * Regla que deja: lo que un spec necesita se importa de un módulo hoja, no del fichero que
 * además cablea la aplicación.
 */
// ============================================================================
//  TcgPreset — el tema de PrimeNG CONSUME los tokens de styles/tokens.scss.
// ============================================================================
//  Ningún color se escribe aquí como literal: cada valor es una referencia CSS
//  (`var(--color-…)`) que PrimeNG emite tal cual en sus variables `--p-*`, así que
//  la resuelve el navegador. Consecuencia: `tokens.scss` es la ÚNICA fuente de
//  color de la aplicación y cambiarla mueve a la vez el CSS propio y PrimeNG.
//
//  Antes de esto el preset traía su propia paleta (rojo casi-igual-pero-no y grises
//  azulados de Tailwind) y los estados semánticos se heredaban de Aura, así que un
//  `p-tag severity="success"` y un chip nuestro eran verdes distintos. El análisis
//  completo está en docs/design-system/auditoria-color.md.
// ============================================================================

/** Escala 50…950 de PrimeNG apuntando a una familia de tokens.scss. */
const escalaDeTokens = (familia: string) => ({
  50:  `var(--color-${familia}-50)`,
  100: `var(--color-${familia}-100)`,
  200: `var(--color-${familia}-200)`,
  300: `var(--color-${familia}-300)`,
  400: `var(--color-${familia}-400)`,
  500: `var(--color-${familia}-500)`,
  600: `var(--color-${familia}-600)`,
  700: `var(--color-${familia}-700)`,
  800: `var(--color-${familia}-800)`,
  900: `var(--color-${familia}-900)`,
  950: `var(--color-${familia}-950)`,
});

// Grises: la escala neutral de BRAND. El 0 y el 950 son los extremos de la nuestra
// (neutral-0 blanco y neutral-1000 negro TCG), que PrimeNG nombra distinto.
const superficie = {
  ...escalaDeTokens('neutral'),
  0:   'var(--color-neutral-0)',
  950: 'var(--color-neutral-1000)',
};

// Familias primitivas de Aura que gobiernan los `severity` de los componentes:
//   success → green | info → sky (botón/tag) y blue (message) | warn → orange
//   (botón/tag) y yellow (message) | danger/error → red | secondary/contrast → surface
// Se remapean a la escala semántica propia para que un p-tag y un chip nuestro
// pinten el mismo verde/ámbar/azul/rojo. `purple` (severity="help") se deja de Aura:
// no se usa en ninguna plantilla.
const primitivosSemanticos = {
  green:  escalaDeTokens('success'),
  sky:    escalaDeTokens('info'),
  blue:   escalaDeTokens('info'),
  orange: escalaDeTokens('warning'),
  yellow: escalaDeTokens('warning'),
  amber:  escalaDeTokens('warning'),
  red:    escalaDeTokens('danger'),
};

/**
 * Lo que TcgPreset aporta sobre Aura. Se exporta para que `app.config.spec.ts`
 * pueda comprobar que aquí no entra ningún literal de color y que todos los tokens
 * referenciados existen en tokens.scss.
 */
export const tcgPresetTokens = {
  primitive: {
    ...primitivosSemanticos,
  },
  semantic: {
    primary: escalaDeTokens('brand'),
    colorScheme: {
      light: {
        surface: superficie,
        primary: {
          // hover/press del botón primario: los tokens dedicados de BRAND, que hasta
          // ahora se aplicaban con CSS a mano en _primeng-overrides.scss.
          hoverColor: 'var(--color-brand-hover)',
          activeColor: 'var(--color-brand-press)',
        },
      },
      // El esquema oscuro se deja como lo trae Aura: no hay escala oscura en
      // tokens.scss ni pantallas que lo usen (auditoría de color §6).
    },
  },
};
